import React, { Component, createRef } from 'react';
import { Tooltip } from '@mui/material';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { TipTapEditor } from '../TipTap';

const CHARACTER_LIMIT = 1073741823; // Java character limit

/**
 * FormMentionEditor - Dual-mode editor with form and workflow placeholders
 * 
 * Switches between:
 * - Text field mode (no toolbar, strips HTML): for simple text inputs
 * - Content block mode (full toolbar, HTML output): for rich text content
 * 
 * Supports both ! (form placeholders) and $ (workflow placeholders).
 * Used in form builders and sequential forms.
 */
class FormMentionEditor extends Component {
  editorRef = createRef();

  constructor(props) {
    super(props);
    this.state = {
      content: '',
      tooltip: '',
    };
  }

  componentDidMount() {
    // Initialize content based on mode
    if (this.props.contentBlock) {
      this.initializeContentBlock();
    } else {
      this.initializeTextField();
    }

    // Set tooltip based on mode and props
    if (!this.props.contentBlock) {
      if (this.props.aiAssistant) {
        this.setState({ tooltip: 'Type "$" to view and insert placeholders' });
      } else if (this.props.assignTask) {
        this.setState({
          tooltip: 'Type "!" to insert a form placeholder or "$" for workflow placeholder',
        });
      } else {
        this.setState({ tooltip: 'Type "!" to insert a form placeholder' });
      }
    }
    if (this.props.disableTooltip) {
      this.setState({ tooltip: '' });
    }
  }

  initializeTextField() {
    let content = this.props.text;
    if (content) {
      content = content.toString();
      content = content.replace(/(?:\r\n|\r|\n\n)/g, '<br>');
      content = content.replace(/(?:\n)/g, '');
    } else {
      content = '';
    }
    this.setState({ content });
  }

  initializeContentBlock() {
    let content = this.props.text;

    if (content) {
      // Normalize table styling
      if (content.includes('table')) {
        content = content
          .replace(/(<table style=")/g, () => {
            return '<table border="1" style="border-collapse:collapse; ';
          })
          .replace(/(border-color:)/g, 'border: 1px solid');
      }
      content = content
        .replace(/(?:\r\n|\r|\n)/g, '<br>')
        .replace(/(<td style="width: [0-9.]+%;">)/g, val => {
          let width = val.match(/([0-9.]+)/g)[0];
          return `<td style="width: ${width}%; padding: 4px;">`;
        });
    } else {
      content = '';
    }

    this.setState({ content });
  }

  componentDidUpdate(prevProps) {
    // Update content if text prop changed externally
    if (this.props.text !== prevProps.text && this.props.text !== this.state.content) {
      this.setState({ content: this.props.text || '' });
    }

    // Handle placeholder insertion via props (for content block mode)
    if (this.props.contentBlock) {
      const { availablePlaceholder } = this.props;
      if (
        availablePlaceholder &&
        availablePlaceholder.length !== prevProps.availablePlaceholder?.length
      ) {
        this.insertPlaceholder(availablePlaceholder[availablePlaceholder.length - 1]);
      }
    }
  }

  insertPlaceholder = (placeholder) => {
    if (placeholder && this.editorRef.current) {
      this.editorRef.current.insertPlaceholder(placeholder);
    }
  };

  handleChange = (value) => {
    this.setState({ content: value });
  };

  handleBlur = (value) => {
    if (this.props.contentBlock) {
      // Content block mode - normalize table HTML
      let normalizedValue = value.replace(/(?:\n)/g, '');
      normalizedValue = normalizedValue
        .replace(/(<table style=")/g, () => {
          return '<table border="1" style="border-collapse:collapse; ';
        })
        .replace(/(border-color:)/g, 'border: 1px solid')
        .replace(/(<td style="width: [0-9.]+%;">)/g, val => {
          let width = val.match(/([0-9.]+)/g)[0];
          return `<td style="width: ${width}%; padding: 4px;">`;
        });

      if (this.props.onChange) {
        this.props.onChange(normalizedValue);
      }
    } else {
      // Text field mode - clean up the value (stripHtml already handles this)
      let cleanValue = value;
      cleanValue = cleanValue.replaceAll('&nbsp;', ' ');

      if (this.props.onChange) {
        this.props.onChange(cleanValue);
      }
    }
  };

  handleFocus = () => {
    if (this.props.onFocus) {
      this.props.onFocus();
    }
  };

  // Determine which trigger characters to use
  getTriggerChars = () => {
    const triggers = ['!']; // Always support form placeholders
    if (this.props.assignTask || this.props.aiAssistant) {
      triggers.push('$'); // Add workflow placeholders if enabled
    }
    return triggers;
  };

  render() {
    const { 
      error, 
      helperText, 
      contentBlock, 
      availablePlaceholders, 
      placeholder,
      style, 
      className 
    } = this.props;
    const { content, tooltip } = this.state;

    return (
      <Tooltip 
        placement="bottom" 
        title={tooltip}
        enterDelay={1200}
        enterNextDelay={1200}
      >
        <div onClick={this.handleFocus} style={style} className={className}>
          <TipTapEditor
            ref={this.editorRef}
            value={content}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            onFocus={this.handleFocus}
            showToolbar={contentBlock || false}
            stripHtml={!contentBlock}
            triggerChars={this.getTriggerChars()}
            availablePlaceholders={availablePlaceholders}
            projectId={useProjectStore.getState().project_id}
            placeholder={placeholder || ''}
            characterLimit={CHARACTER_LIMIT}
            variant={contentBlock ? 'default' : 'minimal'}
            error={error}
            helperText={helperText}
          />
        </div>
      </Tooltip>
    );
  }
}

export default FormMentionEditor;
